import { useState, useEffect, useRef } from 'react';
import { MdMoreVert, MdClose, MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { adToBs } from '../../utils/calendar';
import useImpersonation from '../../hooks/useImpersonation';

// Author initials for the avatar
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();
};

// BS · AD date — reuses the exact Announcements format (adToBs from utils/calendar).
// "3 May 2026"
const fmtAd = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// "Jestha 21, 2083"
const fmtBs = (d) => {
  try { const b = adToBs(d); return `${b.monthName} ${b.day}, ${b.year}`; }
  catch { return fmtAd(d); }
};
// "Jestha 21, 2083 · 3 May 2026"
const fmtBsAd = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  return `${fmtBs(d)} · ${fmtAd(d)}`;
};

// ── Lightbox ──────────────────────────────────────────────────────────────────
function PhotoLightbox({ photos, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handlePrev = () => setCurrentIndex((i) => (i === 0 ? photos.length - 1 : i - 1));
  const handleNext = () => setCurrentIndex((i) => (i === photos.length - 1 ? 0 : i + 1));

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') handlePrev();
      else if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const currentPhoto = photos[currentIndex];
  const hasCaption = currentPhoto?.caption && currentPhoto.caption.trim();

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:bg-white/10 p-2 rounded-lg transition"
      >
        <MdClose className="text-2xl" />
      </button>

      {photos.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-4 text-white hover:bg-white/10 p-2 rounded-lg transition"
          >
            <MdNavigateBefore className="text-3xl" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 text-white hover:bg-white/10 p-2 rounded-lg transition"
          >
            <MdNavigateNext className="text-3xl" />
          </button>
        </>
      )}

      <img
        src={currentPhoto.url}
        alt={`Photo ${currentIndex + 1}`}
        className="max-h-[90vh] max-w-[90vw] object-contain"
      />

      {hasCaption && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 max-w-sm text-white text-sm bg-black/70 px-4 py-2 rounded">
          {currentPhoto.caption}
        </div>
      )}

      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
          {currentIndex + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

// ── Post card ─────────────────────────────────────────────────────────────────
function TrainingJournalCard({ post, onAddFollowUp, onDelete, canDelete }) {
  const { isReadOnly } = useImpersonation();
  const [showMenu, setShowMenu] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const menuRef = useRef(null);

  // Click-outside to close the ⋯ menu
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  const handleDelete = () => {
    setShowMenu(false);
    if (window.confirm('Delete this training post? This action cannot be undone.')) {
      onDelete(post._id);
    }
  };

  const photos = post.photos || [];
  const n = photos.length;

  // A single image tile: crop-to-fill so mismatched aspect ratios always look uniform.
  const PhotoTile = ({ i, className = '', children }) => (
    <button
      type="button"
      onClick={() => setLightboxIndex(i)}
      className={`group relative overflow-hidden rounded-lg bg-nav-hover ${className}`}
    >
      <img
        src={photos[i].url}
        alt={photos[i].caption || `Photo ${i + 1}`}
        loading="lazy"
        className="w-full h-full object-cover transition duration-200 group-hover:scale-[1.02]"
      />
      {children}
    </button>
  );

  const renderPhotos = () => {
    if (n === 1) return <PhotoTile i={0} className="aspect-[16/10]" />;

    if (n === 2) return (
      <div className="grid grid-cols-2 gap-1.5">
        <PhotoTile i={0} className="aspect-[4/3]" />
        <PhotoTile i={1} className="aspect-[4/3]" />
      </div>
    );

    if (n === 3) return (
      <div className="grid grid-cols-2 grid-rows-2 gap-1.5 aspect-[4/3]">
        <PhotoTile i={0} className="row-span-2" />
        <PhotoTile i={1} />
        <PhotoTile i={2} />
      </div>
    );

    // n >= 4 → 2×2; 4th tile shows "+N" when there are extras
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {[0, 1, 2, 3].map((i) => (
          <PhotoTile key={i} i={i} className="aspect-square">
            {i === 3 && n > 4 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <span className="text-white text-lg font-bold">+{n - 4}</span>
              </div>
            )}
          </PhotoTile>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="bg-card border border-divider rounded-xl overflow-hidden">
        <div className="p-4 flex flex-col gap-3">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-white">
                  {getInitials(post.createdByName || post.createdBy?.name)}
                </span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-secondary truncate">
                    {post.createdByName || post.createdBy?.name || 'Unknown'}
                  </h3>
                  <span className="text-xs font-medium bg-nav-hover text-muted px-2 py-0.5 rounded-full flex-shrink-0">
                    {post.createdByRole === 'admin' ? 'Admin' : 'Trainer'}
                  </span>
                </div>
                <p className="text-xs text-subtle mt-0.5" title={fmtBsAd(post.postDate)}>
                  {fmtBsAd(post.postDate)}
                </p>
              </div>
            </div>

            {/* ⋯ menu */}
            {canDelete && (
              <div ref={menuRef} className="relative flex-shrink-0">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 text-muted hover:bg-nav-hover rounded-lg transition-colors"
                >
                  <MdMoreVert className="text-lg" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-card rounded-lg shadow-lg border border-divider z-10 overflow-hidden min-w-max">
                    <button
                      onClick={handleDelete}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-nav-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Delete Post
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Title + content */}
          <div>
            <h2 className="font-semibold text-base text-secondary">{post.title}</h2>
            <p className="text-sm text-muted mt-1.5 whitespace-pre-wrap leading-relaxed">{post.content}</p>
          </div>

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {post.tags.map((tag) => (
                <span key={tag} className="text-xs bg-primary/10 text-secondary px-2.5 py-0.5 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Photos */}
          {n > 0 && <div>{renderPhotos()}</div>}
        </div>

        {/* Footer (only when a follow-up handler is provided) */}
        {onAddFollowUp && (
          <div className="px-4 py-3 border-t border-divider">
            <button
              onClick={() => onAddFollowUp(post._id)}
              disabled={isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className="w-full px-3 py-2 text-sm font-medium text-primary hover:bg-nav-hover rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Follow-up
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <PhotoLightbox
          photos={post.photos}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}

export default TrainingJournalCard;
